import {
  DEVELOPMENT_EXTENSION_ID,
  EDGE_EXTENSION_ID,
  PRODUCTION_EXTENSION_ID,
} from '@/plugin/security';

/**
 * Messages straight to the extension. `chrome.runtime` exists on a page only
 * where an extension lists its origin under `externally_connectable`; ours
 * lists app.clugtm.com, whose cookies it can't read.
 */
type PortalToExtensionMessage =
  | { type: 'CLEANLIST_LOGIN'; token: string }
  | { type: 'CLEANLIST_SIGNED_OUT' }
  | { type: 'CLEANLIST_PING' };

type ExtensionRuntime = {
  sendMessage: (
    extensionId: string,
    message: PortalToExtensionMessage,
    callback: (response: unknown) => void
  ) => unknown;
  lastError?: unknown;
};

const extensionRuntime = (): ExtensionRuntime | null => {
  const runtime = (
    globalThis as { chrome?: { runtime?: Partial<ExtensionRuntime> } }
  ).chrome?.runtime;
  return typeof runtime?.sendMessage === 'function'
    ? (runtime as ExtensionRuntime)
    : null;
};

const extensionIds = (): string[] =>
  [
    PRODUCTION_EXTENSION_ID,
    EDGE_EXTENSION_ID,
    import.meta.env.DEV ? DEVELOPMENT_EXTENSION_ID : undefined,
  ].filter((id): id is string => Boolean(id));

/** Whether this page can message the extension at all. */
export const canMessageExtension = (): boolean => extensionRuntime() !== null;

const sendToExtension = (
  message: PortalToExtensionMessage,
  onResponse?: (response: unknown) => void
): boolean => {
  const runtime = extensionRuntime();
  if (!runtime) return false;
  for (const id of extensionIds()) {
    try {
      const sent = runtime.sendMessage(id, message, (response) => {
        // An id that isn't installed answers through lastError; reading it
        // keeps Chrome from logging it as unchecked.
        void runtime.lastError;
        onResponse?.(response);
      });
      // Safari returns a promise and rejects it ("Tab not found") instead of
      // throwing; unhandled, that reached Datadog as an error.
      Promise.resolve(sent).catch(() => undefined);
    } catch {
      // Disabled, or this id doesn't accept messages from this page.
    }
  }
  return true;
};

export type ExtensionAnswer = { ok: boolean; error?: string };

/** `onAnswer` hears each installed build's reply; ids that aren't installed
 * stay silent. */
export const shareLoginWithExtension = (
  token: string,
  onAnswer?: (answer: ExtensionAnswer) => void
): void => {
  sendToExtension({ type: 'CLEANLIST_LOGIN', token }, (response) => {
    const answer = response as Partial<ExtensionAnswer> | undefined;
    if (typeof answer?.ok === 'boolean')
      onAnswer?.({ ok: answer.ok, error: answer.error });
  });
};

export const shareSignOutWithExtension = (): void => {
  sendToExtension({ type: 'CLEANLIST_SIGNED_OUT' });
};

/** True once any of our extension builds answers, false after `timeoutMs`. */
export const pingExtension = (timeoutMs: number): Promise<boolean> =>
  new Promise((resolve) => {
    let settled = false;
    const finish = (installed: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(installed);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    const sent = sendToExtension({ type: 'CLEANLIST_PING' }, (response) => {
      if ((response as { ok?: unknown } | undefined)?.ok === true) finish(true);
    });
    if (!sent) finish(false);
  });
